import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "./Input";
import { Label } from "./Label";

type MultiSelectOption = {
  id: number;
  label: string;
  detail?: string;
  inactive?: boolean;
};

type MultiSelectDropdownProps = {
  id: string;
  label: string;
  options: MultiSelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  emptyMessage: string;
  searchPlaceholder: string;
  conflictedIds?: number[];
};

export function MultiSelectDropdown({
  id,
  label,
  options,
  selectedIds,
  onChange,
  emptyMessage,
  searchPlaceholder,
  conflictedIds = [],
}: MultiSelectDropdownProps) {
  const [query, setQuery] = useState("");
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const conflicts = new Set(conflictedIds);

  useEffect(() => {
    const closeWhenClickingOutside = (event: PointerEvent) => {
      if (detailsRef.current && !detailsRef.current.contains(event.target as Node)) {
        detailsRef.current.open = false;
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current.open = false;
        detailsRef.current.querySelector<HTMLElement>("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", closeWhenClickingOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeWhenClickingOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("de-DE");
    if (!normalizedQuery) return options;
    return options.filter((option) => `${option.label} ${option.detail ?? ""}`.toLocaleLowerCase("de-DE").includes(normalizedQuery));
  }, [options, query]);

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((selectedId) => selectedId !== id) : [...selectedIds, id]);
  };

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <details ref={detailsRef} className="group relative">
        <summary id={id} className="flex h-10 cursor-pointer list-none items-center justify-between rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 marker:hidden hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500">
          <span>{selectedIds.length ? `${selectedIds.length} ausgewählt` : "Keine Auswahl"}</span>
          <span className="text-slate-400 transition-transform group-open:rotate-180">⌄</span>
        </summary>
        <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          <div className="relative">
            <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} className="h-9 pl-8" />
          </div>
          <div className="mt-2 max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-sm text-slate-500">{query ? "Keine passenden Einträge." : emptyMessage}</div>
            ) : filteredOptions.map((option) => {
              const conflicted = conflicts.has(option.id);
              return (
                <label key={option.id} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm ${conflicted ? "bg-rose-50 text-rose-900 hover:bg-rose-100" : "hover:bg-slate-50"}`}>
                  <input type="checkbox" checked={selectedIds.includes(option.id)} onChange={() => toggle(option.id)} className="h-4 w-4 rounded border-slate-300" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}{option.inactive ? " (inaktiv)" : ""}</span>
                    {conflicted && <span className="block text-xs text-rose-700">Im gewählten Zeitraum belegt</span>}
                  </span>
                  {option.detail && <span className="max-w-32 truncate text-xs text-slate-500">{option.detail}</span>}
                </label>
              );
            })}
          </div>
        </div>
      </details>
    </div>
  );
}
